// /apps/provider/src/components/messages/markup/ImageMarkupTool.jsx

import { useState, useRef, useEffect } from 'react';
import {
  X,
  Save,
  Download,
  Undo,
  Redo,
  Type,
  Circle,
  Square,
  ArrowUp,
  Minus,
  Pen,
  Highlighter,
  Eraser,
  Palette,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Move
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const TOOLS = {
  PEN: 'pen',
  HIGHLIGHTER: 'highlighter',
  ARROW: 'arrow',
  LINE: 'line',
  CIRCLE: 'circle',
  RECTANGLE: 'rectangle',
  TEXT: 'text',
  ERASER: 'eraser',
  MOVE: 'move'
};

const COLORS = [
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#000000', // Black
  '#FFFFFF', // White
  '#FFA500', // Orange
  '#800080', // Purple
];

const STROKE_WIDTHS = [2, 4, 6, 8, 10];

export default function ImageMarkupTool({
  imageUrl,
  imageName = 'image',
  onSave,
  onClose,
  existingAnnotations = null
}) {
  // Canvas refs
  const canvasRef = useRef(null);
  const imageCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const lastTouchX = useRef(null);
  const lastTouchY = useRef(null);

  // State
  const [tool, setTool] = useState(TOOLS.PEN);
  const [color, setColor] = useState('#FF0000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [textValue, setTextValue] = useState('');

  // Load image on mount
  useEffect(() => {
    loadImage();
  }, [imageUrl]);

  // Load existing annotations if provided
  useEffect(() => {
    if (existingAnnotations && imageLoaded) {
      restoreAnnotations(existingAnnotations);
    }
  }, [existingAnnotations, imageLoaded]);

  const loadImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setOriginalImage(img);
      setImageLoaded(true);

      if (canvasRef.current && imageCanvasRef.current) {
        // Account for mobile viewport and toolbars
        const isMobile = window.innerWidth <= 768;
        const maxWidth = containerRef.current?.clientWidth || window.innerWidth;
        const maxHeight = window.innerHeight - (isMobile ? 250 : 200); // More space for mobile toolbars

        let width = img.width;
        let height = img.height;

        // Scale down if needed
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (maxHeight / height) * width;
          height = maxHeight;
        }

        // Account for device pixel ratio
        const dpr = window.devicePixelRatio || 1;

        canvasRef.current.width = width * dpr;
        canvasRef.current.height = height * dpr;
        imageCanvasRef.current.width = width * dpr;
        imageCanvasRef.current.height = height * dpr;

        // Scale canvas back down using CSS
        canvasRef.current.style.width = width + 'px';
        canvasRef.current.style.height = height + 'px';
        imageCanvasRef.current.style.width = width + 'px';
        imageCanvasRef.current.style.height = height + 'px';

        // Draw image on background canvas with proper scaling
        const imgCtx = imageCanvasRef.current.getContext('2d');
        imgCtx.scale(dpr, dpr);
        imgCtx.drawImage(img, 0, 0, width, height);

        // Setup drawing canvas
        const ctx = canvasRef.current.getContext('2d');
        ctx.scale(dpr, dpr);
      }
    };
    img.src = imageUrl;
  };

  const getMousePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    };
  };

  const getTouchPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: (touch.clientX - rect.left) / zoom,
      y: (touch.clientY - rect.top) / zoom
    };
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent scrolling
    const fakeMouseEvent = {
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY
    };
    startDrawing(fakeMouseEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling
    if (!isDrawing && !isPanning) return;

    const touch = e.touches[0];
    const fakeMouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      movementX: touch.clientX - (lastTouchX.current || touch.clientX),
      movementY: touch.clientY - (lastTouchY.current || touch.clientY)
    };

    lastTouchX.current = touch.clientX;
    lastTouchY.current = touch.clientY;

    draw(fakeMouseEvent);
  };

  const startDrawing = (e) => {
    if (tool === TOOLS.MOVE) {
      setIsPanning(true);
      return;
    }

    if (tool === TOOLS.TEXT) {
      const pos = getMousePos(e);
      setTextPosition(pos);
      setShowTextInput(true);
      return;
    }

    setIsDrawing(true);
    const pos = getMousePos(e);
    setStartPoint(pos);

    if (tool === TOOLS.PEN || tool === TOOLS.HIGHLIGHTER || tool === TOOLS.ERASER) {
      setCurrentPath([pos]);
    }
  };

  const draw = (e) => {
    if (!isDrawing && !isPanning) return;

    if (isPanning) {
      // Handle panning
      setPan({
        x: pan.x + e.movementX,
        y: pan.y + e.movementY
      });
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);

    // Clear canvas for shape tools
    if ([TOOLS.ARROW, TOOLS.LINE, TOOLS.CIRCLE, TOOLS.RECTANGLE].includes(tool)) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      redrawHistory();
    }

    // Set drawing styles
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === TOOLS.ERASER) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else if (tool === TOOLS.HIGHLIGHTER) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.globalAlpha = 1;
    }

    // Draw based on tool
    switch (tool) {
      case TOOLS.PEN:
      case TOOLS.HIGHLIGHTER:
      case TOOLS.ERASER:
        currentPath.push(pos);
        ctx.beginPath();
        ctx.moveTo(currentPath[currentPath.length - 2].x, currentPath[currentPath.length - 2].y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        break;

      case TOOLS.LINE:
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        break;

      case TOOLS.ARROW:
        drawArrow(ctx, startPoint.x, startPoint.y, pos.x, pos.y);
        break;

      case TOOLS.CIRCLE:
        const radius = Math.sqrt(
          Math.pow(pos.x - startPoint.x, 2) + Math.pow(pos.y - startPoint.y, 2)
        );
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
        break;

      case TOOLS.RECTANGLE:
        ctx.beginPath();
        ctx.rect(
          startPoint.x,
          startPoint.y,
          pos.x - startPoint.x,
          pos.y - startPoint.y
        );
        ctx.stroke();
        break;
    }
  };

  const stopDrawing = (e) => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (!isDrawing) return;

    setIsDrawing(false);

    // Save to history
    const canvas = canvasRef.current;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);

    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(imageData);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);

    // Reset current path
    setCurrentPath([]);
    setStartPoint(null);
  };

  const drawArrow = (ctx, fromX, fromY, toX, toY) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    // Draw line
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();

    // Draw arrowhead
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 6),
      toY - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 6),
      toY - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  };

  const addText = () => {
    if (!textValue.trim()) {
      setShowTextInput(false);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.font = `${strokeWidth * 4}px Arial`;
    ctx.fillStyle = color;
    ctx.fillText(textValue, textPosition.x, textPosition.y);

    // Save to history
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(imageData);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);

    // Reset text input
    setTextValue('');
    setShowTextInput(false);
  };

  const undo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.putImageData(history[newStep], 0, 0);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.putImageData(history[newStep], 0, 0);
    }
  };

  const redrawHistory = () => {
    if (historyStep >= 0 && history[historyStep]) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.putImageData(history[historyStep], 0, 0);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save to history
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(imageData);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    const imageCanvas = imageCanvasRef.current;

    // Create a new canvas to merge image and annotations
    const mergedCanvas = document.createElement('canvas');
    mergedCanvas.width = canvas.width;
    mergedCanvas.height = canvas.height;
    const mergedCtx = mergedCanvas.getContext('2d');

    // Draw original image
    mergedCtx.drawImage(imageCanvas, 0, 0);

    // Draw annotations
    mergedCtx.drawImage(canvas, 0, 0);

    // Convert to blob
    mergedCanvas.toBlob(async (blob) => {
      // Create file from blob with better naming
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const originalName = imageName.replace(/\.[^/.]+$/, ''); // Remove extension
      const fileName = `${originalName}_annotated_${timestamp}.png`;

      const file = new File([blob], fileName, { type: 'image/png' });

      // Also save annotation data for future editing
      const annotationData = canvas.toDataURL('image/png');

      if (onSave) {
        await onSave(file, annotationData);
      }
    }, 'image/png');
  };

  const restoreAnnotations = (annotationData) => {
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      // Save to history
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setHistory([imageData]);
      setHistoryStep(0);
    };
    img.src = annotationData;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex flex-col">
      {/* Header Toolbar */}
      <div className="bg-white border-b px-2 md:px-4 py-2 flex items-center justify-between">
        {/* Title section - Hidden on mobile */}
        <div className="hidden md:flex items-center gap-2 min-w-0">
          <h3 className="text-lg font-semibold truncate">Image Markup</h3>
          <span className="text-sm text-gray-500 truncate">{imageName}</span>
        </div>

        {/* Mobile title placeholder - Empty div to maintain flex layout */}
        <div className="md:hidden w-0"></div>

        {/* Button section - Same container for both mobile and desktop */}
        <div className="flex items-center gap-1 md:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={undo}
            disabled={historyStep <= 0}
            title="Undo"
            className="p-1.5 md:p-2"
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={redo}
            disabled={historyStep >= history.length - 1}
            title="Redo"
            className="p-1.5 md:p-2"
          >
            <Redo className="h-4 w-4" />
          </Button>

          <div className="hidden md:block border-l mx-2 h-6" />

          <Button
            variant="outline"
            size="sm"
            onClick={clearCanvas}
            title="Clear All"
            className="hidden md:flex"
          >
            Clear
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            className="px-2 py-1.5 md:px-3 md:py-2"
          >
            <Save className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Save & Close</span>
            <span className="md:hidden">Save</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-1.5 md:p-2"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Tool Palette */}
      <div className="bg-gray-100 border-b px-2 md:px-4 py-2 flex items-center gap-2 md:gap-4 overflow-x-auto">
        {/* Drawing Tools - Make scrollable on mobile */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {[
            { id: TOOLS.PEN, icon: Pen, label: 'Pen' },
            { id: TOOLS.HIGHLIGHTER, icon: Highlighter, label: 'Highlighter' },
            { id: TOOLS.ARROW, icon: ArrowUp, label: 'Arrow' },
            { id: TOOLS.LINE, icon: Minus, label: 'Line' },
            { id: TOOLS.CIRCLE, icon: Circle, label: 'Circle' },
            { id: TOOLS.RECTANGLE, icon: Square, label: 'Rectangle' },
            { id: TOOLS.TEXT, icon: Type, label: 'Text' },
            { id: TOOLS.ERASER, icon: Eraser, label: 'Eraser' },
            { id: TOOLS.MOVE, icon: Move, label: 'Pan' }
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setTool(id)}
              className={cn(
                "p-2 rounded hover:bg-gray-200 transition-colors",
                "min-w-[40px] min-h-[40px]", // Touch-friendly size
                tool === id && "bg-white shadow-sm"
              )}
              title={label}
            >
              <Icon className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          ))}
        </div>

        <div className="border-l h-8 flex-shrink-0" />

        {/* Color Picker - Scrollable on mobile */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Palette className="h-4 w-4 text-gray-600 hidden md:block" />
          <div className="flex gap-1">
            {COLORS.slice(0, 6).map(c => ( // Show fewer colors on mobile
              <button
                key={c}
                onClick={() => setColor(c)}
                className={cn(
                  "w-7 h-7 rounded border-2 flex-shrink-0",
                  color === c ? "border-gray-800" : "border-gray-300"
                )}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
            {/* Show remaining colors in dropdown on mobile */}
            <details className="md:hidden relative">
              <summary className="w-7 h-7 rounded border-2 border-gray-300 bg-gray-200 flex items-center justify-center cursor-pointer">
                +
              </summary>
              <div className="absolute right-0 top-8 bg-white shadow-lg rounded p-2 grid grid-cols-3 gap-1 z-10">
                {COLORS.slice(6).map(c => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={cn(
                      "w-7 h-7 rounded border-2",
                      color === c ? "border-gray-800" : "border-gray-300"
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </details>
            {/* Desktop shows all colors */}
            <div className="hidden md:flex gap-1">
              {COLORS.slice(6).map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={cn(
                    "w-6 h-6 rounded border-2",
                    color === c ? "border-gray-800" : "border-gray-300"
                  )}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="border-l h-8 flex-shrink-0" />

        {/* Stroke Width - Simplified on mobile */}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <span className="text-xs md:text-sm text-gray-600 hidden md:inline">Width:</span>
          <select
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(Number(e.target.value))}
            className="px-1 md:px-2 py-1 border rounded text-xs md:text-sm"
          >
            {STROKE_WIDTHS.map(w => (
              <option key={w} value={w}>{w}px</option>
            ))}
          </select>
        </div>

        {/* Zoom controls - Hidden on mobile to save space */}
        <div className="hidden md:flex border-l h-8" />

        <div className="hidden md:flex items-center gap-1">
          <button
            onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(Math.min(3, zoom + 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="p-1 rounded hover:bg-gray-200 ml-1"
            title="Reset View"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Canvas Area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-200 relative"
        style={{
          cursor: tool === TOOLS.MOVE ? 'move' : 'crosshair',
          touchAction: 'none' // Prevent scrolling while drawing
        }}
      >
        <div
          className="relative inline-block"
          style={{
            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
            transformOrigin: 'top left'
          }}
        >
          {/* Background Image Canvas */}
          <canvas
            ref={imageCanvasRef}
            className="absolute top-0 left-0"
            style={{ pointerEvents: 'none' }}
          />

          {/* Annotation Canvas */}
          <canvas
            ref={canvasRef}
            className="relative"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={stopDrawing}
            style={{ cursor: tool === TOOLS.MOVE ? 'move' : 'crosshair' }}
          />

          {/* Text Input */}
          {showTextInput && (
            <div
              className="absolute"
              style={{
                left: textPosition.x,
                top: textPosition.y - 20,
                transform: `scale(${1 / zoom})`
              }}
            >
              <input
                type="text"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addText();
                  if (e.key === 'Escape') setShowTextInput(false);
                }}
                onBlur={addText}
                className="px-2 py-1 border rounded shadow-lg"
                style={{
                  color: color,
                  fontSize: `${strokeWidth * 4}px`
                }}
                placeholder="Type text..."
                autoFocus
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}